import type { LocationReturnUrl } from '@/shared/lib/types/location';
import { H1 } from '@/shared/ui/components/Typography';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import CloseIcon from '@mui/icons-material/Close';
import type { PaperProps as MuiPaperProps, SxProps, Theme } from '@mui/material';
import { Stack, Typography } from '@mui/material';
import type { DialogProps } from '@mui/material/Dialog';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import type { TransitionProps as MuiTransitionProps } from '@mui/material/transitions';
import type { ReactNode } from 'react';
import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// `Omit<..., 'title'>`: DialogProps inherits HTMLAttributes' `title?: string`. Intersecting that
// with our ReactNode title yields `string & ReactNode`, so callers passing JSX were rejected.
export type BaseDialogProps = Omit<DialogProps, 'title'> & {
  title: ReactNode;
  subtitle?: ReactNode;
  customTitleHeader?: boolean; //TODO:  This is the wrong way to do this.  Instead of title being a string, make it a reactnode and pass in custom style to title
  hideDialog?: () => void;
  disableBackdropClick?: boolean;
  canNavigateBack?: boolean; //TODO:  need to improve this, needed to add as not having was sending user back to details page after adding comment
  onClickBack?: () => void | undefined;
  headerElement?: ReactNode;
  titleSx?: SxProps<Theme>;
  hideCloseButton?: boolean;
  // Removed from DialogProps in v9 in favour of `slotProps.paper`; kept here so existing callers
  // don't all need updating, and merged into `slotProps.paper` below. `ref` is omitted: nothing
  // passes one, and its type collides with the slot's own component ref type.
  PaperProps?: Omit<Partial<MuiPaperProps>, 'ref'>;
  // Likewise removed in v9, in favour of `slotProps.transition`. This one is not optional cosmetics:
  // `mui-modal-provider` hands every modal its `onExited` through this prop, and that callback is
  // the only thing that unmounts a modal. See the merge into `slotProps.transition` below.
  TransitionProps?: Partial<MuiTransitionProps>;
};

const BaseDialog = ({
  title,
  subtitle,
  customTitleHeader = false,
  children,
  hideDialog,
  disableBackdropClick = false,
  canNavigateBack = true,
  onClickBack = undefined,
  headerElement,
  titleSx,
  hideCloseButton,
  PaperProps,
  TransitionProps,
  slotProps,
  ...props
}: BaseDialogProps) => {
  const location = useLocation() as LocationReturnUrl;
  const navigate = useNavigate();

  const handleClose = useCallback(
    (event: unknown, reason: 'backdropClick' | 'escapeKeyDown') => {
      if (disableBackdropClick && reason && reason === 'backdropClick') return;
      hideDialog && hideDialog();
    },
    [disableBackdropClick, hideDialog]
  );

  useEffect(() => {
    if (canNavigateBack) {
      return () => {
        const returnUrl = location?.state?.returnUrl || null;
        if (returnUrl) {
          navigate(returnUrl, { state: location?.state?.returnState });
        }
      };
    }
  }, [canNavigateBack, location?.state?.returnUrl, location?.state?.returnState, navigate]);

  // The close (X) button and `headerElement` are independent: the X is absolutely positioned over
  // the title bar's right edge, the header element sits in the title bar's flex flow. Rendering them
  // as two branches of one ternary (as before) silently dropped the header action from any dialog
  // that also had a close button.
  const showCloseButton = Boolean(hideDialog) && !hideCloseButton;

  return (
    <Dialog
      {...props}
      maxWidth={props.maxWidth || 'sm'}
      fullWidth={props.fullWidth}
      onClose={() => handleClose(null, 'backdropClick')}
      slotProps={{
        ...slotProps,
        // `ref` is asserted away below: neither `PaperProps` nor `slotProps.paper` ever carries one
        // here, but their declared types disagree on the ref's target (function component vs. class),
        // which the type checker treats as a real conflict.
        paper: {
          ...PaperProps,
          ...(typeof slotProps?.paper === 'function' ? undefined : slotProps?.paper)
        } as NonNullable<DialogProps['slotProps']>['paper'],
        // `mui-modal-provider` still speaks the pre-v9 API: it passes its `onExited` as
        // `TransitionProps`, and v9's Dialog reads the transition from `slotProps.transition`
        // instead, so the callback never fired. `onExited` is what dispatches DESTROY in the
        // provider, so without this no modal in the app was ever unmounted -- closed dialogs stayed
        // mounted for the session, and the returnUrl navigation in the cleanup below never ran.
        transition: {
          ...(typeof slotProps?.transition === 'function' ? undefined : slotProps?.transition),
          ...TransitionProps
        } as NonNullable<DialogProps['slotProps']>['transition']
      }}
    >
      <DialogTitle
        {...(headerElement && {
          sx: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            // keep the header element clear of the absolutely-positioned close button
            ...(showCloseButton && { pr: 6 }),
            ...titleSx
          }
        })}
      >
        {!!onClickBack && typeof onClickBack === 'function' && (
          <IconButton
            aria-label="back"
            onClick={onClickBack}
            sx={{
              p: 0,
              color: theme => theme.palette.grey[500]
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
        )}
        <Stack>
          {customTitleHeader ? <H1 sx={{ fontWeight: 500, fontSize: '16px', lineHeight: '25px' }}>{title}</H1> : title}
          {subtitle && (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary'
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Stack>

        {headerElement}

        {showCloseButton && (
          <IconButton
            aria-label="close"
            onClick={hideDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme => theme.palette.grey[500]
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      {children}
    </Dialog>
  );
};

export default BaseDialog;
